import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import {
  History, ChevronDown, ChevronRight,
  CheckCircle2, XCircle, Send, Eye,
  RefreshCw, FilePlus, Pencil, Trash2, Settings,
  MessageCircle, MessageCircleReply, MessageCircleOff, CircleDot,
  Users, UserPlus, UserMinus, UserCog,
  FileText, RotateCcw, ArrowUpCircle, GitBranch,
  AlertTriangle,
} from 'lucide-react'
import { EmptyState } from '@/components/shared/empty-state'
import { supabase } from '@/lib/supabase/client'
import { cn } from '@/lib/utils/cn'
import dayjs from 'dayjs'

// ─── Event config ─────────────────────────────────────────────────────────────

type EventConfig = {
  label: string
  icon: React.ComponentType<{ className?: string }>
  color: string
  bg: string
  showDiff: boolean
}

const EVENT_CONFIG: Record<string, EventConfig> = {
  // ── Records ──────────────────────────────────────────────────────────────
  record_created: {
    label: 'ایجاد سابقه',
    icon: FilePlus,
    color: 'text-primary',
    bg: 'bg-primary/10',
    showDiff: false,
  },
  record_approved: {
    label: 'تأیید شد',
    icon: CheckCircle2,
    color: 'text-emerald-400',
    bg: 'bg-emerald-500/10',
    showDiff: false,
  },
  record_declined: {
    label: 'رد شد',
    icon: XCircle,
    color: 'text-red-400',
    bg: 'bg-red-500/10',
    showDiff: false,
  },
  record_final_submitted: {
    label: 'ارسال نهایی',
    icon: Send,
    color: 'text-blue-400',
    bg: 'bg-blue-500/10',
    showDiff: false,
  },
  record_under_review: {
    label: 'شروع بررسی',
    icon: Eye,
    color: 'text-orange-400',
    bg: 'bg-orange-500/10',
    showDiff: false,
  },
  record_status_changed: {
    label: 'تغییر وضعیت',
    icon: RefreshCw,
    color: 'text-muted-foreground',
    bg: 'bg-muted',
    showDiff: true,
  },
  record_resumed_after_decline: {
    label: 'از سر گرفته شد',
    icon: RotateCcw,
    color: 'text-yellow-400',
    bg: 'bg-yellow-500/10',
    showDiff: false,
  },
  record_soft_deleted: {
    label: 'حذف سابقه',
    icon: Trash2,
    color: 'text-red-400',
    bg: 'bg-red-500/10',
    showDiff: false,
  },
  record_restored: {
    label: 'بازیابی سابقه',
    icon: RotateCcw,
    color: 'text-emerald-400',
    bg: 'bg-emerald-500/10',
    showDiff: false,
  },
  record_updated: {
    label: 'ویرایش سابقه',
    icon: Pencil,
    color: 'text-muted-foreground',
    bg: 'bg-muted',
    showDiff: true,
  },
  // ── Submissions ───────────────────────────────────────────────────────────
  form_draft_saved: {
    label: 'ذخیره پیش‌نویس',
    icon: Pencil,
    color: 'text-muted-foreground',
    bg: 'bg-muted',
    showDiff: true,
  },
  form_submitted: {
    label: 'ارسال فرم',
    icon: Send,
    color: 'text-blue-400',
    bg: 'bg-blue-500/10',
    showDiff: false,
  },
  form_resubmitted: {
    label: 'ارسال مجدد فرم',
    icon: Send,
    color: 'text-yellow-400',
    bg: 'bg-yellow-500/10',
    showDiff: false,
  },
  field_value_changed: {
    label: 'تغییر مقدار فیلد',
    icon: GitBranch,
    color: 'text-muted-foreground/70',
    bg: 'bg-muted/50',
    showDiff: true,
  },
  // ── Legacy event names (kept for backward compat) ─────────────────────────
  submission_saved: {
    label: 'ذخیره فرم',
    icon: Pencil,
    color: 'text-muted-foreground',
    bg: 'bg-muted',
    showDiff: true,
  },
  submission_submitted: {
    label: 'ارسال فرم',
    icon: Send,
    color: 'text-blue-400',
    bg: 'bg-blue-500/10',
    showDiff: false,
  },
  record_submitted_for_review: {
    label: 'ارسال برای بررسی',
    icon: Send,
    color: 'text-blue-400',
    bg: 'bg-blue-500/10',
    showDiff: false,
  },
  record_deleted: {
    label: 'حذف سابقه',
    icon: Trash2,
    color: 'text-red-400',
    bg: 'bg-red-500/10',
    showDiff: false,
  },
  // ── Form versioning ───────────────────────────────────────────────────────
  form_published: {
    label: 'انتشار فرم',
    icon: FileText,
    color: 'text-primary',
    bg: 'bg-primary/10',
    showDiff: true,
  },
  form_minor_version_published: {
    label: 'نسخه جزئی منتشر شد',
    icon: FileText,
    color: 'text-primary',
    bg: 'bg-primary/10',
    showDiff: false,
  },
  form_major_version_published: {
    label: 'نسخه اصلی منتشر شد',
    icon: AlertTriangle,
    color: 'text-orange-400',
    bg: 'bg-orange-500/10',
    showDiff: true,
  },
  record_form_version_upgraded: {
    label: 'ارتقاء نسخه فرم',
    icon: ArrowUpCircle,
    color: 'text-blue-400',
    bg: 'bg-blue-500/10',
    showDiff: true,
  },
  // ── Comments ──────────────────────────────────────────────────────────────
  comment_added: {
    label: 'نظر اضافه شد',
    icon: MessageCircle,
    color: 'text-blue-400',
    bg: 'bg-blue-500/10',
    showDiff: false,
  },
  comment_reply_added: {
    label: 'پاسخ داده شد',
    icon: MessageCircleReply,
    color: 'text-blue-400',
    bg: 'bg-blue-500/10',
    showDiff: false,
  },
  comment_resolved: {
    label: 'نظر حل شد',
    icon: CheckCircle2,
    color: 'text-emerald-400',
    bg: 'bg-emerald-500/10',
    showDiff: false,
  },
  comment_reopened: {
    label: 'نظر بازگشایی شد',
    icon: CircleDot,
    color: 'text-yellow-400',
    bg: 'bg-yellow-500/10',
    showDiff: false,
  },
  comment_edited: {
    label: 'نظر ویرایش شد',
    icon: Pencil,
    color: 'text-muted-foreground',
    bg: 'bg-muted',
    showDiff: false,
  },
  comment_deleted: {
    label: 'نظر حذف شد',
    icon: MessageCircleOff,
    color: 'text-red-400',
    bg: 'bg-red-500/10',
    showDiff: false,
  },
  // ── Members ───────────────────────────────────────────────────────────────
  member_added: {
    label: 'عضو اضافه شد',
    icon: UserPlus,
    color: 'text-emerald-400',
    bg: 'bg-emerald-500/10',
    showDiff: false,
  },
  member_removed: {
    label: 'عضو حذف شد',
    icon: UserMinus,
    color: 'text-red-400',
    bg: 'bg-red-500/10',
    showDiff: true,
  },
  member_role_changed: {
    label: 'نقش عضو تغییر کرد',
    icon: UserCog,
    color: 'text-yellow-400',
    bg: 'bg-yellow-500/10',
    showDiff: true,
  },
}

const ROLE_LABELS: Record<string, string> = {
  admin: 'مدیر',
  approver: 'ناظر',
  data_entry: 'ثبت‌کننده',
  viewer: 'مشاهده‌گر',
  super_user: 'ابرکاربر',
}

// ─── Fetch ────────────────────────────────────────────────────────────────────

async function fetchAuditLog(recordId: string) {
  const { data, error } = await supabase
    .from('audit_entries')
    .select('*, actor:user_profiles!actor_user_id(full_name)')
    .eq('record_id', recordId)
    .order('created_at', { ascending: false })
    .limit(100)
  if (error) throw error
  return data ?? []
}

// ─── Inline summary for known event types ─────────────────────────────────────

function EventSummary({ eventType, after }: { eventType: string; after: Record<string, unknown> | null }) {
  if (eventType === 'record_declined' && after?.decline_comment) {
    return (
      <p className="text-xs text-red-300 mt-1.5 leading-relaxed">
        دلیل رد: {after.decline_comment as string}
      </p>
    )
  }
  if (eventType === 'record_approved' && after?.approval_comment) {
    return (
      <p className="text-xs text-emerald-300 mt-1.5 leading-relaxed">
        نظر تأیید: {after.approval_comment as string}
      </p>
    )
  }
  return null
}

// ─── Component ────────────────────────────────────────────────────────────────

export function RecordAuditTab({ recordId, projectId: _projectId }: { recordId: string; projectId: string }) {
  const [expanded, setExpanded] = useState<Set<string>>(new Set())

  const { data: entries = [], isLoading } = useQuery({
    queryKey: ['record-audit', recordId],
    queryFn: () => fetchAuditLog(recordId),
  })

  const toggle = (id: string) => {
    const next = new Set(expanded)
    next.has(id) ? next.delete(id) : next.add(id)
    setExpanded(next)
  }

  if (isLoading) {
    return (
      <div className="space-y-2">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="h-14 bg-muted rounded-xl animate-pulse" />
        ))}
      </div>
    )
  }

  if (entries.length === 0) {
    return <EmptyState icon={History} title="رویدادی ثبت نشده" />
  }

  return (
    <div className="space-y-2">
      {entries.map((entry: Record<string, unknown>) => {
        const eventType = entry.event_type as string
        const config = EVENT_CONFIG[eventType]
        const Icon = config?.icon ?? RefreshCw
        const iconColor = config?.color ?? 'text-muted-foreground'
        const bgColor = config?.bg ?? 'bg-muted'
        const label = config?.label ?? eventType
        const showDiff = config?.showDiff ?? (entry.before_value !== null || entry.after_value !== null)
        const isExpanded = expanded.has(entry.id as string)
        const actor = entry.actor as Record<string, string> | null
        const actorRole = entry.actor_role_at_time as string | null
        const afterValue = entry.after_value as Record<string, unknown> | null
        const beforeValue = entry.before_value as Record<string, unknown> | null
        const hasDiff = showDiff && (beforeValue !== null || afterValue !== null)

        return (
          <div
            key={entry.id as string}
            className="bg-card border border-border rounded-xl overflow-hidden"
          >
            <div
              className={cn('flex items-start gap-3 px-4 py-3', hasDiff && 'cursor-pointer hover:bg-muted/20')}
              onClick={() => hasDiff && toggle(entry.id as string)}
            >
              {/* Icon */}
              <div className={cn('mt-0.5 w-7 h-7 rounded-full flex items-center justify-center shrink-0', bgColor)}>
                <Icon className={cn('w-3.5 h-3.5', iconColor)} />
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className={cn('text-xs font-medium', iconColor)}>{label}</span>
                  {actorRole && (
                    <span className="text-[10px] text-muted-foreground bg-muted px-1.5 py-0.5 rounded">
                      {ROLE_LABELS[actorRole] ?? actorRole}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2 mt-0.5">
                  <span className="text-[11px] text-muted-foreground">
                    {actor?.full_name ?? 'سیستم'}
                  </span>
                  <span className="text-[10px] text-muted-foreground/60">·</span>
                  <span className="text-[11px] text-muted-foreground">
                    {dayjs(entry.created_at as string).format('YYYY/MM/DD HH:mm')}
                  </span>
                </div>
                <EventSummary eventType={eventType} after={afterValue} />
              </div>

              {hasDiff && (
                isExpanded
                  ? <ChevronDown className="w-3.5 h-3.5 text-muted-foreground shrink-0 mt-1" />
                  : <ChevronRight className="w-3.5 h-3.5 text-muted-foreground shrink-0 mt-1 rotate-180" />
              )}
            </div>

            {/* Raw diff — only for edit events */}
            {isExpanded && hasDiff && (
              <div className="border-t border-border px-4 py-3 grid grid-cols-2 gap-3">
                <div>
                  <p className="text-[10px] font-medium text-muted-foreground mb-1.5">قبل</p>
                  <pre className="text-[10px] text-foreground bg-muted rounded p-2 overflow-auto max-h-32 font-mono whitespace-pre-wrap">
                    {JSON.stringify(beforeValue, null, 2) ?? 'null'}
                  </pre>
                </div>
                <div>
                  <p className="text-[10px] font-medium text-muted-foreground mb-1.5">بعد</p>
                  <pre className="text-[10px] text-foreground bg-muted rounded p-2 overflow-auto max-h-32 font-mono whitespace-pre-wrap">
                    {JSON.stringify(afterValue, null, 2) ?? 'null'}
                  </pre>
                </div>
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}
